import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pointer-event drag-to-reorder for a list of rows (no dependency). Rows carry
 * `data-reorder-key`; spread `rowProps(key)` on each row. Press a row and move past
 * `threshold` px to start a drag; the row under the pointer becomes the drop target and
 * the pointer's vertical half of it picks 'above' / 'below'. Releasing calls
 * `onReorder(key, targetKey, position)`; Esc or releasing off a valid target cancels.
 * Presses that start on `[data-no-drag]` elements (checkboxes, sliders, inputs, buttons)
 * never start a drag, and the click that ends a drag is swallowed.
 */

/** 'above' when the pointer is in the upper half of the rect, else 'below'. */
export function dropPosition(clientY, rect) {
  return clientY < rect.top + rect.height / 2 ? 'above' : 'below';
}

const defaultResolve = (x, y) => {
  const el = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
  return el?.closest?.('[data-reorder-key]') || null;
};

export function useDragReorder({ onReorder, canDrop = () => true, threshold = 4, resolveTarget = defaultResolve }) {
  const [drag, setDrag] = useState(null); // { key, overKey, position } while dragging
  const live = useRef({ onReorder, canDrop, resolveTarget });
  live.current = { onReorder, canDrop, resolveTarget };
  const cleanup = useRef(null);

  useEffect(() => () => { if (cleanup.current) cleanup.current(); }, []);

  const onPointerDown = useCallback((e, key) => {
    if (e.button && e.button !== 0) return;
    if (e.target?.closest?.('[data-no-drag]')) return;
    if (cleanup.current) cleanup.current();
    const start = { x: e.clientX, y: e.clientY };
    let dragging = false;
    let over = null; // { key, position }

    const move = (ev) => {
      if (!dragging) {
        if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < threshold) return;
        dragging = true;
      }
      const el = live.current.resolveTarget(ev.clientX, ev.clientY);
      const overKey = el?.getAttribute?.('data-reorder-key') ?? el?.dataset?.reorderKey ?? null;
      if (overKey && overKey !== key && live.current.canDrop(key, overKey)) {
        over = { key: overKey, position: dropPosition(ev.clientY, el.getBoundingClientRect()) };
      } else over = null;
      setDrag({ key, overKey: over?.key ?? null, position: over?.position ?? null });
    };
    const finish = (commit) => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('keydown', esc, true);
      cleanup.current = null;
      if (dragging) {
        // The click that follows a drag release must not toggle anything.
        const swallow = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
        document.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', swallow, true), 0);
        setDrag(null);
        if (commit && over) live.current.onReorder(key, over.key, over.position);
      }
    };
    const up = () => finish(true);
    const cancel = () => finish(false);
    const esc = (ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); finish(false); } };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    document.addEventListener('keydown', esc, true);
    cleanup.current = () => finish(false);
  }, [threshold]);

  const rowProps = useCallback((key) => ({
    'data-reorder-key': key,
    onPointerDown: (e) => onPointerDown(e, key),
  }), [onPointerDown]);

  return { drag, rowProps };
}
